import React, { useState } from 'react';
import { UserProfile, PulseStatus } from '../types';
import { WateenLogo } from './WateenLogo';
import {
  Search,
  Plus,
  Sparkles,
  UserCheck,
  Bell,
  Moon,
  Briefcase,
  Plane,
  Coffee,
  Check,
  Shield,
  Bot,
  Flame,
  Radio,
  Crown,
  Zap,
} from 'lucide-react';

interface WateenHeaderProps {
  currentUser: UserProfile;
  onOpenProfile: () => void;
  onOpenSearch: () => void;
  onOpenNewChat: () => void;
  onOpenNewDirectChat: () => void;
  onOpenAI: () => void;
  onOpenHub?: () => void;
  onUpdatePulse: (pulse: PulseStatus) => void;
  onOpenPremium?: () => void;
}

const PULSE_OPTIONS: Array<{ state: PulseStatus['state']; emoji: string; label: string }> = [
  { state: 'available', emoji: '✨', label: 'متاح للحديث' },
  { state: 'busy', emoji: '⛔', label: 'مشغول حالياً' },
  { state: 'working', emoji: '💻', label: 'في العمل والتركيز' },
  { state: 'sleeping', emoji: '🌙', label: 'نائم أو مسترخٍ' },
  { state: 'traveling', emoji: '✈️', label: 'مسافر في رحلة' },
  { state: 'calm', emoji: '🌿', label: 'في هدوء واسترخاء' },
];

export const WateenHeader: React.FC<WateenHeaderProps> = ({
  currentUser,
  onOpenProfile,
  onOpenSearch,
  onOpenNewChat,
  onOpenNewDirectChat,
  onOpenAI,
  onOpenHub,
  onUpdatePulse,
  onOpenPremium,
}) => {
  const [showPulseMenu, setShowPulseMenu] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-[#080d16]/90 backdrop-blur-xl border-b border-slate-800/80 px-3 sm:px-6 py-2.5 flex items-center justify-between font-['Cairo']">
      {/* Brand & Logo */}
      <div className="flex items-center gap-3">
        <WateenLogo size="md" showText={true} showTagline={true} />
      </div>

      {/* Center Actions: Pulse Status Indicator Pill */}
      <div className="relative">
        <button
          onClick={() => setShowPulseMenu(!showPulseMenu)}
          className="flex items-center gap-2 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] active:scale-95 border border-white/10 hover:border-emerald-500/40 text-xs text-slate-200 transition-all duration-200 shadow-sm group select-none animate-float-gentle"
          title="تغيير نبضك وحالتك على وتين"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-beacon-wave absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-80" />
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 shadow-md shadow-emerald-500/80" />
          </span>
          <span className="text-sm transition-transform duration-200 group-hover:scale-125 inline-block animate-wiggle-soft">{currentUser.pulseStatus?.emoji || '✨'}</span>
          <span className="hidden sm:inline text-xs font-semibold text-slate-300 group-hover:text-emerald-300 max-w-[130px] sm:max-w-[200px] truncate">
            {currentUser.pulseStatus?.text || 'متاح على وتين'}
          </span>
        </button>

        {/* Pulse Selector Dropdown */}
        {showPulseMenu && (
          <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 w-64 bg-[#0e1422] border border-slate-800 rounded-2xl p-2.5 shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-150">
            <div className="text-[11px] font-bold text-slate-400 px-2.5 py-1 mb-1 border-b border-slate-800/60 flex items-center justify-between">
              <span>حدد نبضك الحالي (Wateen Pulse)</span>
              <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
            </div>
            <div className="space-y-1">
              {PULSE_OPTIONS.map((opt) => {
                const isSelected = currentUser.pulseStatus?.state === opt.state;
                return (
                  <button
                    key={opt.state}
                    onClick={() => {
                      onUpdatePulse({ state: opt.state, emoji: opt.emoji, text: opt.label });
                      setShowPulseMenu(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition ${
                      isSelected
                        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold'
                        : 'text-slate-300 hover:bg-[#162030] hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-base">{opt.emoji}</span>
                      <span>{opt.label}</span>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-emerald-400" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Right Controls */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        {/* Wateen Premium Button 👑 */}
        {onOpenPremium && (
          <button
            onClick={onOpenPremium}
            className={`relative group h-9 sm:h-10 px-2.5 sm:px-3 rounded-2xl border flex items-center gap-1.5 text-xs font-bold transition-all duration-300 shadow-sm active:scale-95 shrink-0 overflow-hidden animate-float-gentle ${
              currentUser.isPremium
                ? 'bg-gradient-to-r from-amber-500/20 via-yellow-500/15 to-amber-500/20 border-amber-400/50 text-amber-300 hover:border-amber-300 hover:shadow-amber-500/20'
                : 'bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-amber-500/10 border-amber-500/30 hover:border-amber-400/50 text-amber-300 hover:shadow-amber-500/15'
            }`}
            title="وتين Premium 👑 - المزايا الحصرية"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/20 to-transparent animate-shimmer-sweep pointer-events-none" />
            <Crown
              className={`w-4 h-4 text-amber-400 group-hover:scale-110 group-hover:-rotate-12 transition-transform duration-300 animate-wiggle-soft ${
                currentUser.isPremium ? 'fill-amber-400/60' : ''
              }`}
            />
            <span className="hidden md:inline font-bold">
              {currentUser.isPremium ? 'وتين Premium' : 'جرّب Premium'}
            </span>
            <span className="text-xs group-hover:scale-125 transition-transform duration-300 inline-block animate-wiggle-soft">👑</span>
          </button>
        )}

        {/* Wateen Hub Fast button */}
        {onOpenHub && (
          <button
            onClick={onOpenHub}
            className="relative group h-9 sm:h-10 px-2.5 sm:px-3 rounded-2xl bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-emerald-500/15 hover:from-emerald-500/25 hover:to-teal-500/25 border border-emerald-500/35 hover:border-emerald-400/60 text-emerald-300 flex items-center gap-1.5 text-xs font-bold transition-all duration-300 shadow-sm hover:shadow-emerald-500/20 active:scale-95 shrink-0 overflow-hidden animate-float-gentle"
            title="مركز وتين الشامل (Wateen Hub) 🔥"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-300/15 to-transparent animate-shimmer-sweep pointer-events-none" />
            <Zap className="w-4 h-4 text-emerald-400 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300 animate-pulse" />
            <span className="hidden sm:inline">وتين Hub</span>
            <span className="text-xs group-hover:scale-125 transition-transform duration-300 inline-block animate-wiggle-soft">🔥</span>
          </button>
        )}

        {/* Wateen AI Fast button */}
        <button
          onClick={onOpenAI}
          className="relative group h-9 sm:h-10 px-2.5 sm:px-3 rounded-2xl bg-gradient-to-r from-indigo-500/15 via-purple-500/10 to-indigo-500/15 hover:from-indigo-500/25 hover:to-purple-500/25 border border-indigo-500/30 hover:border-indigo-400/60 text-indigo-300 flex items-center gap-1.5 text-xs font-bold transition-all duration-300 shadow-sm hover:shadow-indigo-500/20 active:scale-95 shrink-0 overflow-hidden animate-float-gentle"
          title="وتين الذكي (AI)"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-indigo-300/20 to-transparent animate-shimmer-sweep pointer-events-none" />
          <div className="relative flex items-center justify-center">
            <Bot className="w-4 h-4 text-indigo-400 group-hover:rotate-12 transition-transform duration-300 animate-wiggle-soft" />
            <span className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-indigo-400 animate-beacon-wave" />
          </div>
          <span className="hidden sm:inline">وتين AI</span>
        </button>

        {/* Global Search */}
        <button
          onClick={onOpenSearch}
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] active:scale-95 border border-white/10 hover:border-white/20 text-slate-300 hover:text-white transition-all duration-200 flex items-center justify-center shadow-sm group shrink-0"
          title="البحث الشامل"
        >
          <Search className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
        </button>

        {/* New Direct Chat */}
        <button
          onClick={onOpenNewDirectChat}
          className="hidden sm:flex w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-white/[0.04] hover:bg-indigo-500/15 active:scale-95 border border-white/10 hover:border-indigo-500/40 text-slate-300 hover:text-indigo-300 transition-all duration-200 items-center justify-center shadow-sm group shrink-0"
          title="محادثة خاصة جديدة (بحث عن صديق)"
        >
          <UserCheck className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
        </button>

        {/* New Group / Channel (+) Button - Modern Living Animated Floating Button */}
        <button
          onClick={onOpenNewChat}
          className="relative group w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-tr from-emerald-600 via-teal-500 to-emerald-400 hover:from-emerald-500 hover:to-teal-400 text-white shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/60 border border-emerald-300/50 transition-all duration-300 active:scale-90 flex items-center justify-center overflow-hidden shrink-0 animate-living-breathe"
          title="إنشاء مجموعة أو قناة جديدة (+)"
        >
          {/* Continuous rotating aura backdrop */}
          <div className="absolute -inset-1 bg-gradient-to-r from-emerald-400 via-teal-300 to-indigo-400 rounded-2xl blur-md opacity-60 animate-pulse-halo -z-10" />
          
          {/* Continuous shimmer light beam */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer-sweep pointer-events-none" />

          {/* Living animated plus icon that breathes and spins slowly */}
          <Plus className="w-5 h-5 transition-transform duration-300 group-hover:rotate-90 group-hover:scale-125 drop-shadow-[0_0_6px_rgba(255,255,255,0.8)] animate-subtle-spin-slow" />
        </button>

        {/* Profile Avatar Pill */}
        <button
          onClick={onOpenProfile}
          className="relative flex items-center gap-2 p-1 pr-2.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] active:scale-95 border border-white/10 hover:border-emerald-500/40 transition-all duration-200 group shrink-0 shadow-sm animate-float-gentle"
          title="الملف الشخصي والإعدادات"
        >
          <div className="relative">
            <img
              src={currentUser.avatar}
              alt={currentUser.displayName}
              className={`w-7 h-7 sm:w-8 sm:h-8 rounded-xl object-cover ring-1 transition-all duration-200 group-hover:scale-105 ${
                currentUser.isPremium ? 'ring-amber-400 ring-2' : 'ring-emerald-500/50'
              }`}
            />
            {currentUser.isPremium && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 text-slate-950 rounded-full flex items-center justify-center text-[9px] font-black shadow-md ring-1 ring-amber-300 animate-wiggle-soft">
                👑
              </span>
            )}
          </div>
          <span className="hidden md:inline text-xs font-bold text-slate-200 group-hover:text-white transition-colors">
            {currentUser.displayName}
          </span>
          {currentUser.isVerified && (
            <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-[9px] text-white shadow-sm shadow-emerald-500/50 animate-pulse">
              ✓
            </span>
          )}
        </button>
      </div>
    </header>
  );
};
